import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import BASE_URL from "../../../config/apiConfig";

const FieldFetchPage = () => {
  const [fieldId, setFieldId] = useState("");
  const [loading, setLoading] = useState(false);
  const [dataReady, setDataReady] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleFetch = async () => {
    setLoading(true);
    setDataReady(false);
    setError("");

    try {
      const fieldRes = await axios.get(
        `${BASE_URL}/api/user/generate-pdf/${fieldId}/`
      );
      const field = fieldRes.data;
      const { latitude, longitude, sowing_date, crop_code, field_area } = field;

      const apiKey = "9425d3f5a8a01fb31de8543e8cce0c71"; // ✅ your OpenWeather key
      const weatherRes = await axios.get(
        `https://api.openweathermap.org/data/3.0/onecall?lat=${latitude}&lon=${longitude}&appid=${apiKey}&units=metric`
      );

      const weather = weatherRes.data;
      const today = weather.daily?.[0] || {}; // Today's data fallback

      // 🔹 3. Extract useful weather metrics
      const weatherData = {
        humidity: today.humidity ?? weather.current?.humidity ?? "N/A",
        rainfall: today.rain ?? 0,
        temperature: today.temp?.day ?? weather.current?.temp ?? "N/A",
        windSpeed: today.wind_speed ?? weather.current?.wind_speed ?? "N/A",
        uvIndex: today.uvi ?? weather.current?.uvi ?? "N/A",
      };

      // 🔹 4. Build report data
      const formattedData = {
        sowingDate: sowing_date,
        cropName: crop_code?.name || "Unknown Crop",
        humidity: weatherData.humidity,
        rainfall: weatherData.rainfall,
        temperature: weatherData.temperature,
        windSpeed: weatherData.windSpeed,
        uvIndex: weatherData.uvIndex,
        fieldId,
        landSize: field_area,
      };

      setReportData(formattedData);
      setDataReady(true);
    } catch (error) {
      console.error(error);
      setError(
        "Failed to fetch field or weather data. Please check the Field ID and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = () => {
    navigate("/admin/generate-report", { state: reportData });
  };

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        background:
          "linear-gradient(to right,rgb(127, 213, 133),rgb(47, 153, 39),rgb(24, 139, 72))",
        p: 4,
      }}
    >
      <Container maxWidth="sm">
        <Card
          elevation={8}
          sx={{ borderRadius: 4, p: 3, backdropFilter: "blur(10px)" }}
        >
          <CardContent>
            <Typography
              variant="h4"
              fontWeight="bold"
              align="center"
              gutterBottom
            >
              🌾 Fetch Field Info
            </Typography>
            <Typography
              variant="subtitle1"
              align="center"
              color="text.secondary"
              mb={4}
            >
              Enter the Field ID to retrieve field and weather information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Field ID"
                  value={fieldId}
                  onChange={(e) => setFieldId(e.target.value)}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleFetch}
                  disabled={loading || !fieldId}
                  sx={{ py: 1.5, fontWeight: "bold", fontSize: "1rem" }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Fetch Field Info"
                  )}
                </Button>
              </Grid>

              {error && (
                <Grid item xs={12}>
                  <Alert severity="error">{error}</Alert>
                </Grid>
              )}

              {dataReady && reportData && (
                <Grid item xs={12}>
                  <Card
                    elevation={6}
                    sx={{
                      mt: 4,
                      p: 3,
                      backgroundColor: "rgba(255,255,255,0.85)",
                      borderRadius: 3,
                    }}
                  >
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      align="center"
                      mb={3}
                    >
                      🌟 Field Report Summary
                    </Typography>

                    <Grid container spacing={2}>
                      {[
                        [
                          { label: "Field ID", value: reportData.fieldId },
                          { label: "Crop Name", value: reportData.cropName },
                          {
                            label: "Sowing Date",
                            value: reportData.sowingDate,
                          },
                          {
                            label: "Land Size",
                            value: `${reportData.landSize} Ha`,
                          },
                          {
                            label: "Humidity",
                            value: `${reportData.humidity}%`,
                          },
                          {
                            label: "Temperature",
                            value: `${reportData.temperature}°C`,
                          },
                          {
                            label: "Rainfall",
                            value: `${reportData.rainfall} mm`,
                          },
                          {
                            label: "Wind Speed",
                            value: `${reportData.windSpeed} m/s`,
                          },
                          { label: "UV Index", value: `${reportData.uvIndex}` },
                        ],
                      ].map((item, index) => (
                        <Grid item xs={12} key={index}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            p={2}
                            borderRadius={2}
                            bgcolor="linear-gradient(90deg, #E0EAFC 0%, #CFDEF3 100%)"
                            boxShadow={1}
                            sx={{
                              background:
                                "linear-gradient(90deg, rgba(224,234,252,1) 0%, rgba(207,222,243,1) 100%)",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                transform: "scale(1.02)",
                                boxShadow: 3,
                              },
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              fontWeight="medium"
                              color="text.secondary"
                            >
                              {item.label}
                            </Typography>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {item.value}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>

                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={handleGenerateReport}
                      sx={{
                        mt: 4,
                        py: 1.5,
                        fontWeight: "bold",
                        background:
                          "linear-gradient(90deg,rgb(147, 236, 114)255, 0) 0%, #92FE9D 100%)",
                        color: "black",
                        "&:hover": {
                          background:
                            "linear-gradient(90deg, #00C9FF 0%, #92FE9D 100%)",
                          opacity: 0.9,
                        },
                      }}
                    >
                      🚀 Generate Report
                    </Button>
                  </Card>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default FieldFetchPage;
